import { supabase } from '../lib/supabase';
import payrollService from './payrollService';
import employeeService from './employeeService';

export const payrollAutomationService = {
  // ============================================================================
  // SCHEDULES
  // ============================================================================

  async getAllSchedules() {
    const { data, error } = await supabase?.from('payroll_schedules')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      scheduleName: row?.schedule_name,
      frequency: row?.frequency,
      departments: row?.departments || [],
      nextRunDate: row?.next_run_date,
      lastRunDate: row?.last_run_date,
      isActive: row?.is_active,
      autoApprove: row?.auto_approve,
      createdBy: row?.created_by,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async createSchedule(schedule) {
    const { data, error } = await supabase?.from('payroll_schedules')?.insert({
        schedule_name: schedule?.scheduleName,
        frequency: schedule?.frequency,
        departments: schedule?.departments,
        next_run_date: schedule?.nextRunDate,
        is_active: schedule?.isActive ?? true,
        auto_approve: schedule?.autoApprove ?? false,
        created_by: schedule?.createdBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      scheduleName: data?.schedule_name,
      frequency: data?.frequency,
      departments: data?.departments,
      nextRunDate: data?.next_run_date,
      isActive: data?.is_active,
      autoApprove: data?.auto_approve
    };
  },

  async updateSchedule(id, schedule) {
    const { data, error } = await supabase?.from('payroll_schedules')?.update({
        schedule_name: schedule?.scheduleName,
        frequency: schedule?.frequency,
        departments: schedule?.departments,
        next_run_date: schedule?.nextRunDate,
        is_active: schedule?.isActive,
        auto_approve: schedule?.autoApprove
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      scheduleName: data?.schedule_name,
      isActive: data?.is_active
    };
  },

  async deleteSchedule(id) {
    const { error } = await supabase?.from('payroll_schedules')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  // ============================================================================
  // BATCH APPROVALS
  // ============================================================================

  async getAllBatchApprovals() {
    const { data, error } = await supabase?.from('payroll_batch_approvals')?.select(`
        *,
        payroll_runs (
          pay_period_start,
          pay_period_end,
          payment_date,
          payroll_status
        )
      `)?.order('submitted_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      payrollRunId: row?.payroll_run_id,
      scheduleId: row?.schedule_id,
      approvalStatus: row?.approval_status,
      submittedBy: row?.submitted_by,
      submittedAt: row?.submitted_at,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      rejectionReason: row?.rejection_reason,
      totalEmployees: row?.total_employees,
      totalAmount: row?.total_amount,
      departmentsIncluded: row?.departments_included || [],
      payrollRun: row?.payroll_runs ? {
        payPeriodStart: row?.payroll_runs?.pay_period_start,
        payPeriodEnd: row?.payroll_runs?.pay_period_end,
        paymentDate: row?.payroll_runs?.payment_date,
        payrollStatus: row?.payroll_runs?.payroll_status
      } : null
    })) || [];
  },

  async createBatchApproval(approval) {
    const { data, error } = await supabase?.from('payroll_batch_approvals')?.insert({
        payroll_run_id: approval?.payrollRunId,
        schedule_id: approval?.scheduleId,
        submitted_by: approval?.submittedBy,
        total_employees: approval?.totalEmployees,
        total_amount: approval?.totalAmount,
        departments_included: approval?.departmentsIncluded
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      payrollRunId: data?.payroll_run_id,
      approvalStatus: data?.approval_status
    };
  },

  async updateBatchApproval(id, approval) {
    const { data, error } = await supabase?.from('payroll_batch_approvals')?.update({
        approval_status: approval?.approvalStatus,
        approved_by: approval?.approvedBy,
        approved_at: approval?.approvedAt,
        rejection_reason: approval?.rejectionReason
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      approvalStatus: data?.approval_status,
      approvedBy: data?.approved_by,
      approvedAt: data?.approved_at
    };
  },

  // ============================================================================
  // PAYSLIPS
  // ============================================================================

  async getAllPayslips() {
    const { data, error } = await supabase?.from('payslips')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      payrollDetailId: row?.payroll_detail_id,
      payrollRunId: row?.payroll_run_id,
      employeeId: row?.employee_id,
      payslipStatus: row?.payslip_status,
      payslipNumber: row?.payslip_number,
      payPeriodStart: row?.pay_period_start,
      payPeriodEnd: row?.pay_period_end,
      paymentDate: row?.payment_date,
      generatedAt: row?.generated_at,
      sentAt: row?.sent_at,
      errorMessage: row?.error_message,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null
    })) || [];
  },

  async getPayslipsByRunId(payrollRunId) {
    const { data, error } = await supabase?.from('payslips')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)?.eq('payroll_run_id', payrollRunId);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      payslipNumber: row?.payslip_number,
      payslipStatus: row?.payslip_status,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null
    })) || [];
  },

  async createPayslip(payslip) {
    const { data, error } = await supabase?.from('payslips')?.insert({
        payroll_detail_id: payslip?.payrollDetailId,
        payroll_run_id: payslip?.payrollRunId,
        employee_id: payslip?.employeeId,
        payslip_number: payslip?.payslipNumber,
        pay_period_start: payslip?.payPeriodStart,
        pay_period_end: payslip?.payPeriodEnd,
        payment_date: payslip?.paymentDate,
        payslip_status: payslip?.payslipStatus || 'draft'
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      payslipNumber: data?.payslip_number,
      payslipStatus: data?.payslip_status
    };
  },

  async updatePayslipStatus(id, status, errorMessage = null) {
    const updateData = {
      payslip_status: status
    };
    
    if (status === 'generated') {
      updateData.generated_at = new Date()?.toISOString();
    } else if (status === 'sent') {
      updateData.sent_at = new Date()?.toISOString();
    }
    
    if (errorMessage) {
      updateData.error_message = errorMessage;
    }
    
    const { data, error } = await supabase?.from('payslips')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      payslipStatus: data?.payslip_status
    };
  },

  // ============================================================================
  // BULK PROCESSING
  // ============================================================================

  async processBulkSalary(scheduleId, departments, createdBy) {
    const logId = crypto.randomUUID();
    
    try {
      // Create processing log
      const { data: logData, error: logError } = await supabase?.from('bulk_processing_logs')?.insert({
          id: logId,
          schedule_id: scheduleId,
          processing_type: 'Bulk Salary Processing',
          departments: departments,
          created_by: createdBy
        })?.select()?.single();
      
      if (logError) throw logError;
      
      // Get employees from specified departments
      let allEmployees = [];
      for (const dept of departments) {
        const deptEmployees = await employeeService?.getByDepartment(dept);
        allEmployees = [...allEmployees, ...deptEmployees];
      }
      
      const totalRecords = allEmployees?.length || 0;
      let successfulRecords = 0;
      let failedRecords = 0;
      const errors = [];
      
      // Process each employee (simplified - in real app would calculate actual payroll)
      for (const employee of allEmployees) {
        try {
          // Simulate processing
          successfulRecords++;
        } catch (err) {
          failedRecords++;
          errors?.push({
            employeeId: employee?.id,
            error: err?.message
          });
        }
      }
      
      // Update log with results
      await supabase?.from('bulk_processing_logs')?.update({
          total_records: totalRecords,
          successful_records: successfulRecords,
          failed_records: failedRecords,
          completed_at: new Date()?.toISOString(),
          error_details: errors?.length > 0 ? errors : null
        })?.eq('id', logId);
      
      return {
        logId,
        totalRecords,
        successfulRecords,
        failedRecords,
        errors
      };
    } catch (error) {
      // Update log with error
      await supabase?.from('bulk_processing_logs')?.update({
          completed_at: new Date()?.toISOString(),
          error_details: [{ error: error?.message }]
        })?.eq('id', logId);
      
      throw error;
    }
  },

  async generateBulkPayslips(payrollRunId) {
    try {
      // Get all payroll details for this run
      const payrollDetails = await payrollService?.getRunDetails(payrollRunId);
      
      if (!payrollDetails || payrollDetails?.length === 0) {
        throw new Error('No payroll details found for this run');
      }
      
      const results = {
        total: payrollDetails?.length,
        successful: 0,
        failed: 0,
        errors: []
      };
      
      // Get payroll run info
      const { data: runData } = await supabase?.from('payroll_runs')?.select('*')?.eq('id', payrollRunId)?.single();
      
      // Generate payslip for each employee
      for (const detail of payrollDetails) {
        try {
          // Get employee info
          const { data: empData } = await supabase?.from('employees')?.select('employee_number')?.eq('id', detail?.employeeId)?.single();
          
          // Generate payslip number
          const payslipNumber = `PS-${new Date(runData?.payment_date)?.toISOString()?.slice(0, 7)?.replace('-', '')}-${empData?.employee_number}`;
          
          // Create payslip
          await this.createPayslip({
            payrollDetailId: detail?.id,
            payrollRunId: payrollRunId,
            employeeId: detail?.employeeId,
            payslipNumber: payslipNumber,
            payPeriodStart: runData?.pay_period_start,
            payPeriodEnd: runData?.pay_period_end,
            paymentDate: runData?.payment_date,
            payslipStatus: 'generated'
          });
          
          results.successful++;
        } catch (err) {
          results.failed++;
          results?.errors?.push({
            employeeId: detail?.employeeId,
            error: err?.message
          });
        }
      }
      
      return results;
    } catch (error) {
      throw error;
    }
  },

  async getBulkProcessingLogs() {
    const { data, error } = await supabase?.from('bulk_processing_logs')?.select('*')?.order('started_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      scheduleId: row?.schedule_id,
      processingType: row?.processing_type,
      departments: row?.departments || [],
      totalRecords: row?.total_records,
      successfulRecords: row?.successful_records,
      failedRecords: row?.failed_records,
      startedAt: row?.started_at,
      completedAt: row?.completed_at,
      errorDetails: row?.error_details,
      createdBy: row?.created_by
    })) || [];
  }
};

export default payrollAutomationService;